// Returns true if all data types that we keep track finished loading:
import { createSelector } from 'reselect';
import * as R from 'ramda';
import { includes, isEmpty } from 'lodash';
import {
  ENTITY_TRAY,
  LAYER_TRAY,
  GROUP_TRAY,
  OVERLAP_TRAY
} from '@constants/map-trays';
import { getFiltersMap } from '@constants/config';
import {
  getConfig,
  getConfigGlobalFilters,
  getConfigFilterToggles,
  getConfigFilterRadios,
  getConfigMapFeatures,
  getConfigEntities
} from '@components/config/selectors';
import {
  getCurrentEntityTypes,
  getEntityType,
  getEntities,
  getOnlyShowOverlaps,
  getOverlapEntities,
  getAgencyTypes,
  getEntityNext,
  getLoadViewport,
  getLoadViewportState,
  getEntity
} from '@selectors/entities-selector';
import { getDataForTypeSelector } from '@selectors/data-types-selector';

import { getSortedUniqueDataType } from '@utils/data-types-utils';
import { processCategories } from '@utils/filter-utils';
import { createUrlFromFilters, getOverlapEntitiesIds } from '@utils/map-utils';

import { sortBy } from '@utils/shared-utils';


export const getMap = state => state.map;
const hasAnyTrueValues = R.pipe(R.values, R.any(value => value === true));
const hasAnyNonEmptyValue = R.pipe(R.values, R.any(value => !isEmpty(value)));
const getMapLoadingFromState = R.defaultTo({}, R.path(['map', 'loading']));
export const getTrays = state => state.map.trays;
export const getTrayManuallyCollapsed = state => state.map.trayManuallyCollapsed;
const getMapModes = state => state.map.modes;
export const getToolbarConfig = state => state.config?.map?.toolbar ?? {};
const getDataTypes = state => state.dataTypes;

// ── Config selectors (read-only from state.config) ─────────────────────────

export const getConfigViewport = createSelector(
  [getConfig],
  config => ({
    center: config?.mapCenter ?? null,
    zoom: config?.map?.zoom?.defaultZoom ?? null
  })
);

// ── Merged selectors (config defaults + mutable state from map-reducer) ────

// Returns config global filters when no user changes exist, otherwise the full
// filter state accumulated by MAP_FILTERS_CHANGED (which already includes the
// config values as its base after the first user interaction).
export const getCurrentMapFilters = createSelector(
  [getConfigGlobalFilters, state => state.map.filters],
  (configFilters, stateFilters) => stateFilters ?? configFilters
);

// Overlays runtime viewport state on top of config defaults for center/zoom.
// bounds, markerItem, etc. are purely runtime and are passed through unchanged.
export const getCurrentViewport = createSelector(
  [getConfigViewport, state => state.map.viewport],
  (configViewport, stateViewport) => ({
    ...stateViewport,
    center: stateViewport.center ?? configViewport.center,
    zoom: stateViewport.zoom ?? configViewport.zoom
  })
);

export const getZoom = createSelector(
  [getCurrentViewport],
  ({zoom}) => zoom
);

export const getMarkerItem = createSelector(
  [getCurrentViewport],
  ({markerItem}) => markerItem
);

export const getDefaultMarkerPosition = createSelector(
  [getCurrentViewport],
  ({defaultMarkerPosition}) => defaultMarkerPosition
);

// Config-toggled types serve as defaults; user toggle actions are sparse
// overrides stored in state.map.filterToggles.
export const getCurrentFilterToggles = createSelector(
  [getConfigFilterToggles, state => state.map.filterToggles],
  (configToggles, stateToggles) => ({ ...configToggles, ...stateToggles })
);

// Config-derived radio state serves as defaults; MAP_SET_MAP_FILTER_RADIO
// stores updated radio state for each entity type in state.map.filterRadios.
export const getCurrentFilterRadios = createSelector(
  [getConfigFilterRadios, state => state.map.filterRadios],
  (configRadios, stateRadios) => ({ ...configRadios, ...stateRadios })
);

export const getCurrentTraffic = createSelector(
  [getConfigMapFeatures, state => state.map.traffic],
  (features, stateTraffic) => stateTraffic ?? features.traffic ?? false
);

export const getCurrentBicycle = createSelector(
  [getConfigMapFeatures, state => state.map.bicycle],
  (features, stateBicycle) => stateBicycle ?? features.bicycle ?? false
);

export const getCurrentTransit = createSelector(
  [getConfigMapFeatures, state => state.map.transit],
  (features, stateTransit) => stateTransit ?? features.transit ?? false
);

export const getEnabledEntityTypes = createSelector(
  [
    getConfigEntities,
    getCurrentFilterToggles
  ],
  (entities, filterToggles) => entities.filter(type => filterToggles[type])
);

const getIds = (overlap, entityName) => {
  if (!isEmpty(overlap) && !isEmpty(overlap[entityName]) && Array.isArray(overlap[entityName])) {
    return overlap[entityName].map(entity => entity.id);
  }
  return [];
};

const getLayerIds = overlap => {
  const ids = {};
  if (overlap) {
    for (const key of Object.keys(overlap)) {
      // Layers are within the overlaps, but we share that space with entities thus skip them:
      if (key !== 'entities') {
        ids[key] = getIds(overlap, key);
      }
    }
  }
  return ids;
};

export const isAnyMapItemLoading = createSelector([getMapLoadingFromState], loading => hasAnyTrueValues(loading));

const DEFAULT_TRAY_TOP = {};
const DEFAULT_TRAY_SECOND = {};
const DEFAULT_TRAY_HOVER = {};
const DEFAULT_TRAY_OVERLAP = {};

export const getTrayTop = createSelector([getTrays], trays => trays[trays.length - 1] || DEFAULT_TRAY_TOP);
export const getTraySecond = createSelector([getTrays], trays => trays[trays.length - 2] || DEFAULT_TRAY_SECOND);
export const getTrayDepth = createSelector([getTrays], trays => trays.length);
export const getTrayHover = createSelector([getTrayTop], ({ hover }) => hover || DEFAULT_TRAY_HOVER);
export const getTraySelectedSegmentId = createSelector([getTrayTop], R.pathOr(null, ['selectedSegment', 'id']));

const getTrayOverlap = createSelector(
  [getTrayTop], ({trayType, data}) => (trayType === OVERLAP_TRAY && data.overlap) || DEFAULT_TRAY_OVERLAP
);

export const isAnyTrayItemHovered = createSelector([getTrayHover], hovering => hasAnyNonEmptyValue(hovering));

const getTrayEntity = createSelector(
  [getTrayTop], ({trayType, data}) => (trayType === ENTITY_TRAY && data.entity) || null
);

export const getTrayEntitiesIds = createSelector(
  [getTrayEntity, getTrayOverlap], (entity, overlap) => entity ? [entity.id] : getIds(overlap, 'entities')
);

export const getHoverEntityId = createSelector([getTrayHover], R.pathOr(null, ['entity', 'id']));

export const getTrayLayer = createSelector(
  [getTrayTop], ({trayType, data}) => (trayType === LAYER_TRAY && data.layer) || null
);

export const getTrayLayersIds = createSelector(
  [getTrayTop, getTrayLayer, getTrayOverlap],
  (tray, layer, overlap) => layer ? {[tray.data.type]: [layer.id]} : getLayerIds(overlap)
);

export const getHoverLayerId = createSelector([getTrayHover], R.pathOr(null, ['layer', 'id']));

export const getTrayGroup = createSelector(
  [getTrayTop], ({trayType, data}) => (trayType === GROUP_TRAY && data.group) || null
);

export const getTrayGroupIds = createSelector(
  [getTrayGroup, getTrayOverlap], (group, overlap) => group ? [group.id] : getIds(overlap, 'groups')
);

export const getHoverGroupId = createSelector([getTrayHover], R.pathOr(null, ['group', 'id']));

export const getMapModesSet = createSelector(
  [getMapModes],
  modes => new Set(modes)
);

// ── Selectors moved from entities-selector (depend on map-selector values) ──

const EMPTY_ENTITIES = [];

export const getEnabledEntities = createSelector(
  [getCurrentEntityTypes, getEnabledEntityTypes],
  (entities, enabledTypes) => {
    const enabledEntities = {};
    Object.keys(entities).forEach(type => {
      if (includes(enabledTypes, type)) {
        enabledEntities[type] = entities[type].list;
      }
    });
    return enabledEntities;
  }
);

export const boundsEntitiesSelector = createSelector([
  getEntities,
  getCurrentViewport,
  getOnlyShowOverlaps,
  getOverlapEntities,
  getEnabledEntityTypes,
  getEntityType
], (
  entities,
  {bounds},
  onlyShowOverlaps,
  overlapEntities,
  enabledTypes,
  type
) => {
  if (bounds && includes(enabledTypes, type)) {
    const result = entities.filter(({ bounds: entityBounds }) => bounds.intersects(entityBounds));
    if (onlyShowOverlaps) {
      if (R.isEmpty(overlapEntities)) {
        return EMPTY_ENTITIES;
      }
      const ids = getOverlapEntitiesIds(overlapEntities);
      return result.filter(({ id }) => includes(ids, id));
    }
    return result;
  }
  return EMPTY_ENTITIES;
});

export const boundsAllEntitiesSelector = createSelector([
  getEnabledEntities,
  getCurrentViewport,
  getOnlyShowOverlaps,
  getOverlapEntities
], (
  entities,
  {bounds},
  onlyShowOverlaps,
  overlapEntities
) => {
  if (bounds) {
    let flatEntities = [];
    Object.keys(entities).forEach(entityType => {
      flatEntities = [...flatEntities, ...entities[entityType]];
    });
    const result = flatEntities.filter(({ bounds: entityBounds }) => bounds.intersects(entityBounds));
    if (onlyShowOverlaps) {
      if (R.isEmpty(overlapEntities)) {
        return EMPTY_ENTITIES;
      }
      const ids = getOverlapEntitiesIds(overlapEntities);
      return result.filter(({ id }) => includes(ids, id));
    }
    return result;
  }
  return EMPTY_ENTITIES;
});

const getSortedUniqueCategory = (list, types, sortField) => {
  const uniqueIds = new Set();
  list.forEach(entity => {
    if (entity.category_dict) {
      entity.category_dict.forEach(category => uniqueIds.add(category.id));
    }
  });
  const existing = [...uniqueIds].map(id => types[id]);
  return sortBy(R.values(existing), sortField);
};

export const categoriesSelector = createSelector([
  boundsEntitiesSelector,
  getDataForTypeSelector('map_category')
], (
  entities,
  categories
) => getSortedUniqueCategory(entities, categories, 'name'));

export const agencyTypesSelector = createSelector([
  boundsAllEntitiesSelector,
  getAgencyTypes
], (
  entities,
  agencyTypes
) => getSortedUniqueDataType('agencyTypeId', entities, agencyTypes, 'name'));

const getEntitySuppressedMapFilters = createSelector(
  [getEntityType, getCurrentFilterRadios],
  (entityType, filterRadios) => {
    const suppressedFilters = [];
    Object.values(filterRadios[entityType] || {}).forEach(radioType => {
      if (radioType.checked) {
        radioType.suppressedFilters?.forEach(suppressed => suppressedFilters.push(suppressed));
      }
    });
    return suppressedFilters;
  }
);

const getEntityMapFilters = createSelector(
  [getEntity],
  entity => entity.filters || null
);

// Return the entity filters, removing the suppressed ones
// (i.e. ones disabled in the UI due to radio button selections).
export const entityMapFiltersSelector = createSelector(
  [getEntityMapFilters, getEntitySuppressedMapFilters],
  (entityFilters, suppressed) => {
    const newFilters = { ...entityFilters };
    suppressed.forEach(name => {
      delete newFilters[name];
    });
    return newFilters;
  }
);

// Used to determine if entity filters have changed from default
// for use in displaying a warning on the overlaps tab in entity trays.
export const getEntityMapFiltersChanged = createSelector(
  [entityMapFiltersSelector, getDataTypes, getEntityType],
  (entityFilters, dataTypes, type) => {
    const filter = getFiltersMap()[`${type}Filters`];
    let validAgencyTypes = false;
    let validCategories = false;
    if (filter) {
      if (filter.agency_type && filter.agency_type[0]) {
        const selectedAgencyTypes = entityFilters[filter.agency_type[0]];
        validAgencyTypes = (
          (!selectedAgencyTypes && typeof selectedAgencyTypes !== 'string') ||
          Object.keys(dataTypes.agency_type).map(
            id => String(selectedAgencyTypes).split(',')
              .indexOf(id) > -1
          )
            .indexOf(false) === -1
        );
      }
      if (filter.map_category && filter.map_category[0]) {
        const selectedCategories = entityFilters[filter.map_category[0]];
        validCategories = (
          (!selectedCategories && typeof selectedCategories !== 'string') ||
          Object.keys(dataTypes.map_category).map(
            id => String(selectedCategories).split(',')
              .indexOf(id) > -1
          )
            .indexOf(false) === -1
        );
      }
    }
    return !(validAgencyTypes && validCategories);
  }
);

export const entityFiltersUrlSelector = createSelector(
  [entityMapFiltersSelector, getCurrentMapFilters, getEntityNext, getLoadViewport, getLoadViewportState, getEntityType],
  (entityFilters, map, next, viewport, inViewport, type) => {
    if (next) {
      return next;
    }
    const newFilters = { ...entityFilters, ...map, type };
    const { shape_selection } = newFilters;
    if (viewport && !shape_selection) {
      if (inViewport) {
        newFilters.geom_in = viewport;
        delete newFilters.geom_not_in;
      } else {
        newFilters.geom_not_in = viewport;
        delete newFilters.geom_in;
      }
    }
    const filters = processCategories(type, { ...newFilters });
    return createUrlFromFilters(filters, 'map');
  }
);
